import { CheckCircleFilled } from '@ant-design/icons';
import { L } from '@lib';
import { Tag } from 'antd';
import React from 'react';

export const LocalizedServiceOrderStatus = {
  Todo: L('To do'),
  PartsNotOrdered: L('To do - Parts not ordered'),
  PartsOrdered: L('To do - Parts ordered'),
  ReadyToGo: L('To do - Ready to go'),
  InProgress: L('In progress'),
  Approval: L('Estimate - Waiting approval'),
  Approved: L('Estimate - Approved'),
  OnHold: L('On hold'),
  Canceled: L('Cancelled'),
  Completed: L('Service completed'),
  Ready: L('Ready for payment'),
  Paid: L('Paid'),
};

/** What "vehicle on site" means - shown beside the toggle wherever it can be set. */
export const VEHICLE_ON_SITE_HINT = L(
  'Mark Yes once the customer has brought the vehicle to the garage. A car icon then shows on the job in the calendar.'
);

export const OrderStatus = {
  Todo: 'Todo',
  PartsNotOrdered: 'PartsNotOrdered',
  PartsOrdered: 'PartsOrdered',
  ReadyToGo: 'ReadyToGo',
  InProgress: 'InProgress',
  Approval: 'Approval',
  Approved: 'Approved',
  OnHold: 'OnHold',
  Canceled: 'Cancelled',
  Completed: 'Completed',
  Ready: 'Ready',
  Paid: 'Paid',
};

/**
 * The statuses a job can have while it waits in the side panel - mirrors
 * OrderStatus.PendingScheduling on the API, the only ones GetAllUnassignedAsync returns. Anything
 * else and the card comes off the panel, whether the change arrived by push or by a save here.
 */
export const PENDING_SCHEDULING_STATUSES: string[] = [
  OrderStatus.Todo,
  OrderStatus.PartsNotOrdered,
  OrderStatus.PartsOrdered,
  OrderStatus.Approval,
  OrderStatus.Approved,
];

export const DropOffDateStatus = {
  Todo: 'Todo',
  PartsNotOrdered: 'PartsNotOrdered',
  PartsOrdered: 'PartsOrdered',
  ReadyToGo: 'ReadyToGo',
  OnHold: 'OnHold',
};

export const ServiceOrderInvoiceStatus = {
  Ready: 'Ready',
  Paid: 'Paid',
};

export const ServiceOrderStatusColor = {
  Todo: '#CDE7FF',
  PartsNotOrdered: '#CDE7FF',
  PartsOrdered: '#CDE7FF',
  Approval: '#FFF1C1',
  Approved: '#DEFFCE',
  InProgress: '#FFF1C1',
  OnHold: '#FFDCDC',
  Completed: '#DEFFCE',
  ReadyToGo: '#CDE7FF',
  Ready: '#DEFFCE',
  Paid: '#F6F6F6',
  Canceled: '#FFDCDC',
};

// Calendar cards, per status group (Estimates purple, To do blue, In progress yellow, On hold pink,
// Completed/Payment green, Paid grey, Cancelled red). Three strengths of one hue, so the status
// reads at a glance without the board turning into a wall of colour:
//   badge - strong: the status tag, the left stripe, the icon
//   card  - pale: the card's outline
//   soft  - barely there (the pale tint at ~45%): the card's own background
// Kept apart from ServiceOrderStatusColor, which tables, the side panel and the order view use.
const CALENDAR_PURPLE = { card: '#EBDCFF', soft: '#F6EFFF', badge: '#7B2FE0' };
const CALENDAR_BLUE = { card: '#D6E9FF', soft: '#EDF5FF', badge: '#2A7BCB' };
const CALENDAR_YELLOW = { card: '#FFEFC2', soft: '#FFF8E4', badge: '#C48A12' };
const CALENDAR_PINK = { card: '#F9D6DD', soft: '#FCEDF0', badge: '#CC4F6E' };
const CALENDAR_GREEN = { card: '#DAFBCF', soft: '#EEFDE9', badge: '#2A8B47' };
const CALENDAR_GREY = { card: '#E6E7EA', soft: '#F4F4F6', badge: '#6B7587' };
const CALENDAR_RED = { card: '#FFDEDE', soft: '#FFF0F0', badge: '#E02424' };

export const CalendarStatusStyle = {
  Approval: { ...CALENDAR_PURPLE, label: 'Awaiting Approval' },
  Approved: { ...CALENDAR_PURPLE, label: 'Approved' },
  Todo: { ...CALENDAR_BLUE, label: 'To Do' },
  PartsNotOrdered: { ...CALENDAR_BLUE, label: 'Parts Not Ordered' },
  PartsOrdered: { ...CALENDAR_BLUE, label: 'Parts Ordered' },
  ReadyToGo: { ...CALENDAR_BLUE, label: 'Ready to Go' },
  InProgress: { ...CALENDAR_YELLOW, label: 'In Progress' },
  OnHold: { ...CALENDAR_PINK, label: 'On Hold' },
  Completed: { ...CALENDAR_GREEN, label: 'Service Completed' },
  Ready: { ...CALENDAR_GREEN, label: 'Ready for Payment' },
  Paid: { ...CALENDAR_GREY, label: 'Paid' },
  Canceled: { ...CALENDAR_RED, label: 'Cancelled' },
};
export type CalendarStatusStyleObjectKey = keyof typeof CalendarStatusStyle;

// Left-hand hint icon on a calendar card (hover for the job's details). In the status colour the
// card sets as --status-stripe (see statusCardStyle); grey where there is none.
const calendarHintIcon = (
  <CheckCircleFilled style={{ fontSize: 18, color: 'var(--status-stripe, rgba(0, 0, 0, 0.65))' }} />
);
export const ServiceOrderStatusIcon = {
  Todo: calendarHintIcon,
  PartsNotOrdered: calendarHintIcon,
  PartsOrdered: calendarHintIcon,
  Approval: calendarHintIcon,
  Approved: calendarHintIcon,
  InProgress: calendarHintIcon,
  OnHold: calendarHintIcon,
  Completed: calendarHintIcon,
  ReadyToGo: calendarHintIcon,
  Ready: calendarHintIcon,
  Paid: calendarHintIcon,
  Canceled: calendarHintIcon,
};

const tagStyle = { minWidth: 200, textAlign: 'center' as const };

export const ServiceOrderStatusTag = {
  Todo: (
    <Tag color="blue" style={tagStyle}>
      To do
    </Tag>
  ),
  PartsNotOrdered: (
    <Tag color="blue" style={tagStyle}>
      To do - Parts not ordered
    </Tag>
  ),
  PartsOrdered: (
    <Tag color="geekblue" style={tagStyle}>
      To do - Parts ordered
    </Tag>
  ),
  ReadyToGo: (
    <Tag color="cyan" style={tagStyle}>
      To do - Ready to go
    </Tag>
  ),
  Approval: (
    <Tag color="purple" style={tagStyle}>
      Estimate - Waiting approval
    </Tag>
  ),
  Approved: (
    <Tag color="green" style={tagStyle}>
      Estimate - Approved
    </Tag>
  ),
  InProgress: (
    <Tag color="orange" style={tagStyle}>
      In progress
    </Tag>
  ),
  OnHold: (
    <Tag color="magenta" style={tagStyle}>
      On hold
    </Tag>
  ),
  Completed: (
    <Tag color="green" style={tagStyle}>
      Completed
    </Tag>
  ),
  Ready: (
    <Tag color="lime" style={tagStyle}>
      Ready for payment
    </Tag>
  ),
  Paid: (
    <Tag color="volcano" style={tagStyle}>
      Paid
    </Tag>
  ),
  Canceled: (
    <Tag color="red" style={tagStyle}>
      Cancelled
    </Tag>
  ),
};

export const ServiceOrderHistoryStatus = {
  Completed: 'Completed',
  Ready: 'Ready',
  Paid: 'Paid',
};

export type ServiceOrderHistoryStatusObjectKey = keyof typeof ServiceOrderHistoryStatus;
export type ServiceOrderStatusColorObjectKey = keyof typeof ServiceOrderStatusColor;
export type ServiceOrderStatusTagObjectKey = keyof typeof ServiceOrderStatusTag;
export type LocalizedServiceOrderStatusObjectKey = keyof typeof LocalizedServiceOrderStatus;
export type ServiceOrderStatusIconObjectKey = keyof typeof ServiceOrderStatusIcon;
export type ServiceOrderInvoiceStatusObjectKey = keyof typeof ServiceOrderInvoiceStatus;
export type DropOffDateStatusObjectKey = keyof typeof DropOffDateStatus;

/**
 * The same tag, sized to its text.
 *
 * ServiceOrderStatusTag is padded to 200px so every tag in a table column lines up; outside a table
 * - in a panel header, next to a title - that padding is just dead space. Cloning keeps one source
 * of truth for the label and the colour.
 */
export const serviceOrderStatusTagCompact = (status: string) => {
  const tag = ServiceOrderStatusTag[status as ServiceOrderStatusTagObjectKey];
  if (!tag) return null;
  return React.cloneElement(tag, { style: { ...tag.props.style, minWidth: 0, margin: 0 } });
};
